export interface ProgressEntry {
  done: boolean;
  updatedAt: string;
  updatedBy?: { userId: string; userDetails: string };
}

export interface AdminSubmissionRow {
  id: string;
  submissionDate: string;
  submittedAt: string;
  submittedBy: { userId: string; userDetails: string };
  data: Record<string, unknown>;
  progress?: Record<string, ProgressEntry>;
}

export interface SubmissionHistoryEntry {
  data: Record<string, unknown>;
  savedAt: string;
  savedBy?: { userId: string; userDetails: string };
}

export interface AdminSubmission {
  id: string;
  submissionDate: string;
  submittedAt: string;
  submittedBy: { userId: string; userDetails: string };
  data: Record<string, unknown>;
  progress?: Record<string, ProgressEntry>;
  editedAt?: string;
  editedBy?: { userId: string; userDetails: string };
  history?: SubmissionHistoryEntry[];
}

export interface ProgressStage {
  id: string;
  label: string;
}

export async function listSubmissions(): Promise<AdminSubmissionRow[]> {
  const r = await fetch("/api/manage/submissions");
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getSubmission(id: string, submissionDate?: string): Promise<AdminSubmission> {
  const url = submissionDate
    ? `/api/manage/submissions/${id}?submissionDate=${encodeURIComponent(submissionDate)}`
    : `/api/manage/submissions/${id}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateSubmission(id: string, submissionDate: string, data: Record<string, unknown>): Promise<void> {
  const r = await fetch(`/api/manage/submissions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data, submissionDate })
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function setSubmissionProgress(
  id: string,
  submissionDate: string,
  stageId: string,
  done: boolean
): Promise<AdminSubmission> {
  const r = await fetch(`/api/manage/submissions/${id}/progress`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ submissionDate, stageId, done })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteSubmission(id: string, submissionDate: string): Promise<void> {
  const r = await fetch(`/api/manage/submissions/${id}?submissionDate=${encodeURIComponent(submissionDate)}`, {
    method: "DELETE"
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function restoreSubmissionVersion(
  id: string,
  submissionDate: string,
  versionIndex: number
): Promise<AdminSubmission> {
  const r = await fetch(`/api/manage/submissions/${id}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ submissionDate, versionIndex })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function emailSubmission(
  id: string,
  submissionDate: string,
  recipients: string[],
  note?: string
): Promise<{ sent: boolean; recipients: string[]; cc: string[] }> {
  const r = await fetch(`/api/manage/submissions/${id}/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ submissionDate, recipients, note })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getProgressStages(): Promise<ProgressStage[]> {
  const r = await fetch("/api/manage/settings/progress-stages");
  if (!r.ok) return [];
  const j = (await r.json()) as { stages: ProgressStage[] };
  return j.stages ?? [];
}

export async function saveProgressStages(stages: ProgressStage[]): Promise<ProgressStage[]> {
  const r = await fetch("/api/manage/settings/progress-stages", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ stages })
  });
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as { stages: ProgressStage[] };
  return j.stages;
}

import type { SectionConfig } from "../formConfig";

export async function fetchFormConfigSections(): Promise<SectionConfig[] | null> {
  try {
    const r = await fetch("/api/manage/settings/form-config");
    if (!r.ok) return null;
    const j = (await r.json()) as { sections: SectionConfig[] | null };
    return Array.isArray(j.sections) ? j.sections : null;
  } catch { return null; }
}

export async function saveFormConfigSections(sections: SectionConfig[]): Promise<SectionConfig[]> {
  const r = await fetch("/api/manage/settings/form-config", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sections })
  });
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as { sections: SectionConfig[] };
  return j.sections;
}

export interface AdminsDoc {
  admins: string[];
  superAdmins: string[];
  permissions?: Record<string, string[]>;
}

export async function getAdmins(): Promise<AdminsDoc> {
  const r = await fetch("/api/manage/settings/admins");
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as AdminsDoc;
  return { admins: j.admins ?? [], superAdmins: j.superAdmins ?? [], permissions: j.permissions ?? {} };
}

export async function saveAdmins(
  admins: string[],
  superAdmins: string[],
  permissions?: Record<string, string[]>
): Promise<AdminsDoc> {
  const r = await fetch("/api/manage/settings/admins", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ admins, superAdmins, permissions: permissions ?? {} })
  });
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as AdminsDoc;
  return { admins: j.admins ?? [], superAdmins: j.superAdmins ?? [], permissions: j.permissions ?? {} };
}

export interface NotificationSettings {
  recipients: string[];
  cc: string[];
  from: string;
  rules: NotificationRecipientRule[];
}

export type NotificationRuleOperator =
  | "equals"
  | "notEquals"
  | "in"
  | "notIn"
  | "contains"
  | "doesNotContain"
  | "isEmpty"
  | "isNotEmpty";

export interface NotificationRecipientRule {
  id: string;
  fieldId: string;
  operator: NotificationRuleOperator;
  value?: string;
  recipients: string[];
  cc: string[];
}

async function notificationSettingsError(response: Response): Promise<Error> {
  if (response.status === 401) {
    return new Error("Your session has expired. Please sign in again and reload the page.");
  }
  if (response.status === 403) {
    return new Error("You don't have permission to manage notifications.");
  }
  return new Error(`Unable to load notifications (${response.status}).`);
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  const r = await fetch("/api/manage/settings/notification-recipients");
  if (!r.ok) throw await notificationSettingsError(r);
  const j = (await r.json()) as Partial<NotificationSettings>;
  return {
    recipients: j.recipients ?? [],
    cc: j.cc ?? [],
    from: j.from ?? "",
    rules: j.rules ?? []
  };
}

export async function saveNotificationSettings(settings: NotificationSettings): Promise<NotificationSettings> {
  const r = await fetch("/api/manage/settings/notification-recipients", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings)
  });
  if (!r.ok) throw await notificationSettingsError(r);
  const j = (await r.json()) as Partial<NotificationSettings>;
  return {
    recipients: j.recipients ?? [],
    cc: j.cc ?? [],
    from: j.from ?? "",
    rules: j.rules ?? []
  };
}

// Backwards-compatible helpers (recipients only).
export async function getNotificationRecipients(): Promise<string[]> {
  return (await getNotificationSettings()).recipients;
}

export async function saveNotificationRecipients(recipients: string[]): Promise<string[]> {
  const current = await getNotificationSettings();
  const saved = await saveNotificationSettings({ ...current, recipients });
  return saved.recipients;
}

export interface CheckpointSummary {
  id: string;
  label: string;
  createdAt: string;
  createdBy: string;
  submissionCount: number;
  hasFormConfig: boolean;
  hasSettings: boolean;
}

export interface RestoreOptions {
  restoreSubmissions?: boolean;
  restoreFormConfig?: boolean;
  restoreSettings?: boolean;
}

export async function listCheckpoints(): Promise<CheckpointSummary[]> {
  const r = await fetch("/api/manage/checkpoints");
  if (!r.ok) return [];
  const j = (await r.json()) as { checkpoints: CheckpointSummary[] };
  return j.checkpoints ?? [];
}

export async function createCheckpoint(label: string): Promise<CheckpointSummary[]> {
  const r = await fetch("/api/manage/checkpoints", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label })
  });
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as { checkpoints: CheckpointSummary[] };
  return j.checkpoints ?? [];
}

export async function deleteCheckpoint(id: string): Promise<CheckpointSummary[]> {
  const r = await fetch(`/api/manage/checkpoints/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(await r.text());
  const j = (await r.json()) as { checkpoints: CheckpointSummary[] };
  return j.checkpoints ?? [];
}

export async function restoreCheckpoint(id: string, options: RestoreOptions): Promise<void> {
  const r = await fetch(`/api/manage/checkpoints/${id}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(options)
  });
  if (!r.ok) throw new Error(await r.text());
}

export interface DatabaseInfo {
  server: string | null;
  database: string;
  schema: string;
  authMode: "connection-string" | "managed-identity" | "unconfigured";
  connected: boolean;
  serverVersion: string | null;
  counts: { submissions: number; drafts: number; settings: number } | null;
  error: string | null;
}

export async function getDatabaseInfo(): Promise<DatabaseInfo> {
  const r = await fetch("/api/manage/db-info");
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
